// /tools/npi-lookup — the free, no-signup NPI lookup tool (the ad landing page).
// Fieldbook language via design-tokens.css. Consumer-usable above the fold, with
// a developer bridge that converts the occasional dev without nagging the biller.
//
// Data: live against the public, rate-limited GET /public/npi/{npi} endpoint
// (no signup). The initial card is a clearly-labeled example so the page paints
// without an API call (prerender-safe) — a real lookup replaces it on demand.
import { useState, useCallback } from "react";
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import { trackGA4Event } from "@/lib/analytics";
import "@/styles/design-tokens.css";
import "./npi-lookup.css";

// Empty VITE_API_URL → relative paths through the dev proxy; otherwise direct.
const API_BASE = import.meta.env.VITE_API_URL ?? "https://devapi.fhirfly.io";

interface Result {
  npi: string;
  entityType: string;
  name: string;
  status: string;
  enumerationDate: string;
  lastUpdated: string;
  taxonomyCode: string;
  taxonomyDisplay: string;
  grouping: string;
  address: string;
  city: string;
  state: string;
  postal: string;
  phone: string;
}

// A real provider, shown as a labeled example on first paint (no fetch).
const EXAMPLE: Result = {
  npi: "1023011178",
  entityType: "Organization",
  name: "Collabria Care",
  status: "Active",
  enumerationDate: "2005-05-23",
  lastUpdated: "2025-05-15",
  taxonomyCode: "251G00000X",
  taxonomyDisplay: "Community Based Hospice Care Agency",
  grouping: "Agencies",
  address: "414 S Jefferson St",
  city: "Napa",
  state: "CA",
  postal: "94559-4515",
  phone: "(707) 258-9080",
};

const DEFAULT_NPI = EXAMPLE.npi;

function titleCase(s: string): string {
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function formatPhone(raw?: string): string {
  const d = (raw || "").replace(/\D/g, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : raw || "—";
}

function formatPostal(raw?: string): string {
  const d = (raw || "").replace(/\D/g, "");
  return d.length === 9 ? `${d.slice(0, 5)}-${d.slice(5)}` : raw || "";
}

// Map the API response (shape=standard&include=display) to the card's fields.
function mapResponse(npi: string, data: any): Result {
  const tax = (data.taxonomies || []).find((t: any) => t.primary) || data.taxonomies?.[0] || {};
  const addr = data.practice_address || {};
  const display = data.display || {};
  const line = [addr.line1, addr.line2].filter(Boolean).join(", ");
  return {
    npi,
    entityType: data.entity_type === "organization" ? "Organization" : "Individual",
    name: display.full_name || data.organization_name || "—",
    status: data.is_active ? "Active" : "Inactive",
    enumerationDate: data.enumeration_date || "—",
    lastUpdated: data.last_update_date || "—",
    taxonomyCode: tax.code || "—",
    taxonomyDisplay: display.primary_specialty || tax.display_name || tax.classification || "—",
    grouping: tax.grouping || "—",
    address: line ? titleCase(line) : "—",
    city: addr.city ? titleCase(addr.city) : "",
    state: addr.state || "",
    postal: formatPostal(addr.postal),
    phone: formatPhone(addr.phone || addr.phone_digits),
  };
}

export default function NpiLookupPage() {
  const [npi, setNpi] = useState(DEFAULT_NPI);
  const [result, setResult] = useState<Result | null>(EXAMPLE);
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const run = useCallback(async (override?: string) => {
    const q = (override ?? npi).trim();
    if (override) setNpi(override);
    if (q.length !== 10) {
      setError("Enter a 10-digit NPI number.");
      return;
    }
    setLoading(true);
    setError(null);
    let result: string;
    try {
      const res = await fetch(`${API_BASE}/public/npi/${q}?shape=standard&include=display`);
      if (res.status === 404) {
        setResult(null);
        setError(`No provider found for NPI ${q}.`);
        result = "not_found";
      } else if (res.status === 429) {
        setError("You've hit the free rate limit — try again in a minute, or grab a free API key for higher limits.");
        result = "rate_limited";
      } else if (res.status === 400) {
        setError("That doesn't look like a valid NPI. NPIs are 10 digits.");
        result = "invalid";
      } else if (!res.ok) {
        setError("Something went wrong looking that up. Please try again.");
        result = "error";
      } else {
        const body = await res.json();
        setResult(mapResponse(q, body.data || body));
        setIsLive(true);
        result = "found";
      }
    } catch {
      setError("Couldn't reach the lookup service. Please try again.");
      result = "error";
    } finally {
      setLoading(false);
    }
    trackGA4Event("npi_lookup_used", { result, mode: "live", tool: "npi_lookup" });
  }, [npi]);

  // Copy-paste curl for the exact record on screen (real prod endpoint).
  const curlCmd = result
    ? `curl "https://api.fhirfly.io/v1/npi/${result.npi}" \\\n  -H "x-api-key: YOUR_API_KEY"`
    : "";

  const copyCurl = useCallback(() => {
    if (!curlCmd) return;
    navigator.clipboard?.writeText(curlCmd).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    });
    trackGA4Event("tool_cta_click", { cta: "copy_curl", location: "npi_lookup" });
  }, [curlCmd]);

  return (
    <div className="ffly ffly--public npi-page">
      <PageMeta
        title="Free NPI Lookup — National Provider Identifier search"
        description="Free NPI number lookup. Search any National Provider Identifier and get the provider name, specialty, and practice address — enriched from CMS NPPES. No sign-up."
        canonical={canonicalUrl("/tools/npi-lookup")}
      />

      <FieldHeader active="tools" />

      <div className="wrap">
        <section className="intro">
          <div className="crumb">Free tool · no sign-up</div>
          <h1>NPI Lookup</h1>
          <p className="lede">
            Enter a National Provider Identifier and get the provider, specialty, and practice
            address — <em>names, not just codes</em>, enriched daily from CMS NPPES.
          </p>

          <div className="search">
            <span className="lab">NPI №</span>
            <input
              value={npi}
              onChange={(e) => setNpi(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="Enter a 10-digit NPI"
              maxLength={10}
              aria-label="NPI number"
            />
            <button onClick={() => run()}>{loading ? "…" : "Look up"}</button>
          </div>
          <div className="hint">
            Don't have a number? Try <button type="button" className="trynum" onClick={() => run(DEFAULT_NPI)}>{DEFAULT_NPI}</button> — or search by name &amp; state (coming soon).
          </div>
          {error && <div className="lookup-error" role="alert">{error}</div>}
        </section>

        {result && (
          <>
            <section className="result">
              <div className="specimen">
                <span className="tag"><span className="pin" />Specimen № {result.npi}</span>
                <h2 className="name">{result.name}</h2>
                <p className="latin">{result.taxonomyDisplay} — {result.entityType}</p>
                <div className="rows">
                  <div className="row"><span className="k">Status</span><span className="v">{result.status} · since {result.enumerationDate}</span></div>
                  <div className="row"><span className="k">Taxonomy</span><span className="v mono">{result.taxonomyCode}</span></div>
                  <div className="row"><span className="k">Grouping</span><span className="v">{result.grouping}</span></div>
                  <div className="row"><span className="k">Address</span><span className="v">{result.address}</span></div>
                  <div className="row"><span className="k">City / State</span><span className="v">{result.city}, {result.state} {result.postal}</span></div>
                  <div className="row"><span className="k">Phone</span><span className="v mono">{result.phone}</span></div>
                </div>
                <div className="attr">{isLive ? "Live record" : "Example record"} · Source: CMS NPPES · NUCC taxonomy · Updated daily.</div>
              </div>

              <div className="note">
                <div className="k">Field notes</div>
                <div className="b"><span className="sw" style={{ background: "#3f8a58" }} />Active provider, verified daily</div>
                <div className="b"><span className="sw" style={{ background: "#38afa7" }} />Specialty enriched from NUCC taxonomy</div>
                <div className="b"><span className="sw" style={{ background: "#d6a224" }} />Type 2 — organization</div>
              </div>
            </section>

            <div className={`bridge${isLive ? " bridge--live" : ""}`}>
              {isLive && (
                <div className="bridge-hit" role="status" data-testid="npi-live-hit">
                  <span className="hit-check" aria-hidden="true">✓</span>
                  <span>
                    You just made a live API call — the same endpoint you'd call in code.
                    Grab a free key to run it programmatically.
                  </span>
                </div>
              )}
              <div className="bridge-top">
                <span className="tiny">This result came from one API call — do it in your code:</span>
                <div className="curl">
                  <pre><code>{curlCmd}</code></pre>
                  <button type="button" className="copy" onClick={copyCurl} aria-label="Copy curl command">
                    {copied ? "Copied ✓" : "Copy"}
                  </button>
                </div>
                <span className="freetier">Free tier: 10,000 requests/mo · all 21+ data types · no credit card</span>
              </div>

              <div className="bridge-cta">
                <a
                  className="dev-cta primary"
                  href="/dashboard"
                  onClick={() => trackGA4Event("tool_cta_click", { cta: "get_api_key", location: "npi_lookup" })}
                >Get a free API key →</a>
                <a
                  className="dev-cta ghost"
                  href="/tools/api-playground"
                  onClick={() => trackGA4Event("tool_cta_click", { cta: "open_playground", location: "npi_lookup" })}
                >Try in playground</a>
              </div>

              <a
                className="mcp-bridge"
                href="/docs/mcp/overview"
                onClick={() => trackGA4Event("tool_cta_click", { cta: "add_mcp", location: "npi_lookup" })}
              >
                <span className="mcp-k">Building an AI agent?</span> Add FHIRfly to Claude via MCP — providers, drugs &amp; codes as tools →
              </a>
            </div>
          </>
        )}
      </div>

      <FieldFooter variant="full" />
    </div>
  );
}
